import type { Article } from './types';

const article: Article = {
  slug: 'usfans-spreadsheet-safety',
  order: 11,
  image:
    'https://readdy.ai/api/search-image?query=A%20flat%20lay%20of%20a%20padlock%20shield%20icon%20checklist%20and%20a%20smartphone%20showing%20secure%20browsing%20on%20a%20soft%20mint%20and%20ivory%20background%20clean%20concept%20of%20online%20safety%20and%20scam%20prevention&width=800&height=600&seq=art-safety-11&orientation=landscape',
  en: {
    title: 'USFans Spreadsheet Safety Guide',
    cardDesc: 'How to verify links, sellers and information safely.',
    lead:
      'Safety starts with good habits. This guide covers practical steps for verifying links, sellers and product information so you can research with more confidence.',
    category: 'Safety',
    readingTime: '7 min read',
    sections: [
      {
        h2: 'Verify Links Before Clicking',
        paragraphs: [
          'Before clicking a link, check where it actually leads. Official links go to the correct domain, while suspicious links may redirect to lookalike pages.',
        ],
      },
      {
        h2: 'Check Sellers Carefully',
        paragraphs: [
          'Look at what a seller is offering and whether the details are consistent. Compare with other sellers and confirm the current information on the main website.',
        ],
      },
      {
        h2: 'Protect Your Personal Information',
        paragraphs: [
          'Never share passwords or sensitive personal information. Be cautious with any request that asks for more than is necessary to complete a normal transaction.',
        ],
      },
      {
        h2: 'Recognize Common Red Flags',
        paragraphs: [
          'Unusually low prices, urgent requests and unofficial pages that copy a real brand are common warning signs. When something feels off, slow down and verify.',
        ],
      },
    ],
    comparisonTitle: 'Verifying Links vs. Verifying Sellers',
    comparisonHeaders: ['Check', 'Verifying Links', 'Verifying Sellers'],
    comparisonRows: [
      { label: 'Focus', a: 'Where it leads', b: 'Who is selling' },
      { label: 'Method', a: 'Check the domain', b: 'Compare details' },
      { label: 'Goal', a: 'Avoid fake pages', b: 'Confirm reliability' },
    ],
    faqs: [
      {
        q: 'How do I verify a link?',
        a: 'Check the domain before clicking and confirm it matches the official website.',
      },
      {
        q: 'What should I never share?',
        a: 'Never share passwords or sensitive personal information with anyone.',
      },
      {
        q: 'What are common red flags?',
        a: 'Unusually low prices, urgent requests and unofficial pages that copy a real brand.',
      },
    ],
  },
  pl: {
    title: 'Przewodnik bezpieczeństwa USFans Spreadsheet',
    cardDesc: 'Jak bezpiecznie weryfikować linki, sprzedawców i informacje.',
    lead:
      'Bezpieczeństwo zaczyna się od dobrych nawyków. Ten przewodnik obejmuje praktyczne kroki weryfikacji linków, sprzedawców i informacji o produktach.',
    category: 'Bezpieczeństwo',
    readingTime: '7 min czytania',
    sections: [
      {
        h2: 'Weryfikuj linki przed kliknięciem',
        paragraphs: [
          'Przed kliknięciem linku sprawdź, dokąd faktycznie prowadzi. Oficjalne linki prowadzą do właściwej domeny, a podejrzane mogą przekierowywać na strony-podróbki.',
        ],
      },
      {
        h2: 'Sprawdzaj sprzedawców uważnie',
        paragraphs: [
          'Zobacz, co oferuje sprzedawca i czy szczegóły są spójne. Porównaj z innymi sprzedawcami i potwierdź aktualne informacje na głównej stronie.',
        ],
      },
      {
        h2: 'Chroń dane osobowe',
        paragraphs: [
          'Nigdy nie udostępniaj haseł ani wrażliwych danych. Uważaj na prośby o więcej informacji, niż jest potrzebne do normalnej transakcji.',
        ],
      },
      {
        h2: 'Rozpoznawaj sygnały ostrzegawcze',
        paragraphs: [
          'Wyjątkowo niskie ceny, pilne prośby i nieoficjalne strony kopiujące prawdziwą markę to częste ostrzeżenia. Gdy coś wydaje się podejrzane, zwolnij i zweryfikuj.',
        ],
      },
    ],
    comparisonTitle: 'Weryfikacja linków a weryfikacja sprzedawców',
    comparisonHeaders: ['Kontrola', 'Weryfikacja linków', 'Weryfikacja sprzedawców'],
    comparisonRows: [
      { label: 'Skupienie', a: 'Dokąd prowadzi', b: 'Kto sprzedaje' },
      { label: 'Metoda', a: 'Sprawdź domenę', b: 'Porównaj szczegóły' },
      { label: 'Cel', a: 'Unikaj fałszywych stron', b: 'Potwierdź wiarygodność' },
    ],
    faqs: [
      {
        q: 'Jak zweryfikować link?',
        a: 'Sprawdź domenę przed kliknięciem i potwierdź, że pasuje do oficjalnej strony.',
      },
      {
        q: 'Czego nigdy nie udostępniać?',
        a: 'Nigdy nie udostępniaj haseł ani wrażliwych danych osobowych.',
      },
      {
        q: 'Jakie są częste sygnały ostrzegawcze?',
        a: 'Wyjątkowo niskie ceny, pilne prośby i nieoficjalne strony kopiujące prawdziwą markę.',
      },
    ],
  },
  es: {
    title: 'Guía de seguridad de USFans Spreadsheet',
    cardDesc: 'Cómo verificar enlaces, vendedores e información de forma segura.',
    lead:
      'La seguridad empieza con buenos hábitos. Esta guía cubre pasos prácticos para verificar enlaces, vendedores e información de productos con más confianza.',
    category: 'Seguridad',
    readingTime: '7 min de lectura',
    sections: [
      {
        h2: 'Verifica los enlaces antes de hacer clic',
        paragraphs: [
          'Antes de hacer clic, revisa a dónde lleva realmente. Los enlaces oficiales van al dominio correcto, mientras que los sospechosos pueden redirigir a páginas falsas.',
        ],
      },
      {
        h2: 'Revisa a los vendedores con cuidado',
        paragraphs: [
          'Mira qué ofrece un vendedor y si los detalles son consistentes. Compara con otros vendedores y confirma la información en el sitio principal.',
        ],
      },
      {
        h2: 'Protege tu información personal',
        paragraphs: [
          'Nunca compartas contraseñas ni información sensible. Desconfía de solicitudes que pidan más de lo necesario para una transacción normal.',
        ],
      },
      {
        h2: 'Reconoce las señales de alerta',
        paragraphs: [
          'Precios inusualmente bajos, solicitudes urgentes y páginas no oficiales que copian una marca real son señales comunes. Si algo parece raro, ve despacio y verifica.',
        ],
      },
    ],
    comparisonTitle: 'Verificar enlaces vs. verificar vendedores',
    comparisonHeaders: ['Comprobación', 'Verificar enlaces', 'Verificar vendedores'],
    comparisonRows: [
      { label: 'Enfoque', a: 'A dónde lleva', b: 'Quién vende' },
      { label: 'Método', a: 'Revisa el dominio', b: 'Compara detalles' },
      { label: 'Objetivo', a: 'Evitar páginas falsas', b: 'Confirmar fiabilidad' },
    ],
    faqs: [
      {
        q: '¿Cómo verifico un enlace?',
        a: 'Revisa el dominio antes de hacer clic y confirma que coincide con el sitio oficial.',
      },
      {
        q: '¿Qué nunca debo compartir?',
        a: 'Nunca compartas contraseñas ni información personal sensible.',
      },
      {
        q: '¿Cuáles son las señales de alerta comunes?',
        a: 'Precios inusualmente bajos, solicitudes urgentes y páginas no oficiales que copian una marca real.',
      },
    ],
  },
  fr: {
    title: 'Guide de sécurité USFans Spreadsheet',
    cardDesc: 'Comment vérifier liens, vendeurs et informations en toute sécurité.',
    lead:
      'La sécurité commence par de bonnes habitudes. Ce guide couvre les étapes pratiques pour vérifier liens, vendeurs et informations produits en toute confiance.',
    category: 'Sécurité',
    readingTime: '7 min de lecture',
    sections: [
      {
        h2: 'Vérifiez les liens avant de cliquer',
        paragraphs: [
          'Avant de cliquer, regardez où mène réellement le lien. Les liens officiels vont vers le bon domaine, tandis que les liens suspects peuvent rediriger vers des pages copiées.',
        ],
      },
      {
        h2: 'Vérifiez soigneusement les vendeurs',
        paragraphs: [
          'Regardez ce qu’un vendeur propose et si les détails sont cohérents. Comparez avec d’autres vendeurs et confirmez l’information sur le site principal.',
        ],
      },
      {
        h2: 'Protégez vos informations personnelles',
        paragraphs: [
          'Ne partagez jamais vos mots de passe ni informations sensibles. Méfiez-vous des demandes qui en exigent plus que nécessaire pour une transaction normale.',
        ],
      },
      {
        h2: 'Reconnaissez les signaux d’alerte',
        paragraphs: [
          'Des prix anormalement bas, des demandes urgentes et des pages non officielles qui copient une vraie marque sont des signes fréquents. En cas de doute, ralentissez et vérifiez.',
        ],
      },
    ],
    comparisonTitle: 'Vérifier les liens ou vérifier les vendeurs',
    comparisonHeaders: ['Vérification', 'Vérifier les liens', 'Vérifier les vendeurs'],
    comparisonRows: [
      { label: 'Objectif', a: 'Où il mène', b: 'Qui vend' },
      { label: 'Méthode', a: 'Vérifier le domaine', b: 'Comparer les détails' },
      { label: 'But', a: 'Éviter les fausses pages', b: 'Confirmer la fiabilité' },
    ],
    faqs: [
      {
        q: 'Comment vérifier un lien ?',
        a: 'Vérifiez le domaine avant de cliquer et confirmez qu’il correspond au site officiel.',
      },
      {
        q: 'Que ne jamais partager ?',
        a: 'Ne partagez jamais vos mots de passe ni informations personnelles sensibles.',
      },
      {
        q: 'Quels sont les signaux d’alerte fréquents ?',
        a: 'Des prix anormalement bas, des demandes urgentes et des pages non officielles qui copient une vraie marque.',
      },
    ],
  },
};

export default article;